/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";
import _setTimeout from "@babel/runtime-corejs3/core-js-stable/set-timeout";
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import _slicedToArray from "@babel/runtime-corejs3/helpers/esm/slicedToArray";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";

/** @module react/components/carousel-slide */
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { animated, useSprings } from 'react-spring';
import { useGesture } from 'react-use-gesture';
import resetClassNames from '../../css/reset.module.scss';
import { mergeClassNames } from '../../modules/merge-class-names.js';
import { mergeStyles } from '../../modules/merge-styles.js';
import componentClassNames from './index.module.scss';

/**
 * @typedef carouselSlideDimensionsType
 * @property {(string|number)} height - Slide height.
 * @property {(string|number)} width - Slide width.
 * @property {(string|number)} [minHeight=null] - Slide minimum height.
 * @property {(string|number)} [minWidth=null] - Slide minimum width.
 * @property {(string|number)} [maxHeight=null] - Slide maximum height.
 * @property {(string|number)} [maxWidth=null] - Slide maximum width.
 */

/**
 * @typedef carouselSlidePropsType
 * @property {string} [direction='row'] - Slide direction.
 * @property {number} [startingPosition=0] - Slide starting position.
 * @property {carouselSlideDimensionsType} [dimensions] - Slide dimensions.
 * @property {number} [drift=0] - Drift for items separation.
 * @property {number} [offset=0] - Offset for items separation.
 * @property {Function} [onMount=null] - Callback for componentDidMount event.
 * @property {Function} [onNewPosition=null] - Callback for newPosition event.
 * @property {*} [children=null] - React prop.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @description A simple yet powerful carousel slide component.
 * @param {carouselSlidePropsType} props - Component props.
 * @returns {Object<string,*>} React component.
 */
function CarouselSlide(props) {
  var _this = this,
      _context,
      _context2,
      _context4,
      _context5,
      _context7,
      _context8,
      _context9,
      _context12,
      _context13;

  var direction = props.direction,
      startingPosition = props.startingPosition,
      dimensions = props.dimensions,
      drift = props.drift,
      offset = props.offset,
      showOverflow = props.showOverflow,
      disableGestures = props.disableGestures,
      onMount = props.onMount,
      onNewPosition = props.onNewPosition,
      children = props.children,
      style = props.style,
      className = props.className,
      customStyles = props.customStyles,
      customClassNames = props.customClassNames,
      globalTheme = props.globalTheme;

  var _ref = dimensions || {},
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? 100 : _ref$height,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? 100 : _ref$width,
      _ref$minHeight = _ref.minHeight,
      minHeight = _ref$minHeight === void 0 ? null : _ref$minHeight,
      _ref$minWidth = _ref.minWidth,
      minWidth = _ref$minWidth === void 0 ? null : _ref$minWidth,
      _ref$maxHeight = _ref.maxHeight,
      maxHeight = _ref$maxHeight === void 0 ? null : _ref$maxHeight,
      _ref$maxWidth = _ref.maxWidth,
      maxWidth = _ref$maxWidth === void 0 ? null : _ref$maxWidth;

  var clampPosition = useCallback(_bindInstanceProperty(_context = function _context(value) {
    _newArrowCheck(this, _this);

    if (value >= children.length) {
      value = children.length - 1;
    } else if (value < 0) {
      value = 0;
    }

    return value;
  }).call(_context, this), [children.length]);
  var position = useRef(clampPosition(startingPosition));
  var rootRef = useRef(null);
  var isDragging = useRef(false);
  var isDraggingTimer = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isGrabbing = _useState2[0],
      setIsGrabbing = _useState2[1];

  var isRow = direction === 'row';
  var positionKey = isRow ? 'left' : 'top';
  var getPositionValue = useCallback(_bindInstanceProperty(_context2 = function _context2(i, delta) {
    var _context3;

    _newArrowCheck(this, _this);

    var slot = i - position.current;
    var percent = 50 + slot * 50 * (1 - drift);
    var constant = slot * offset + (delta || 0);
    return _concatInstanceProperty(_context3 = "calc(".concat(percent, "% + ")).call(_context3, constant, "px)");
  }).call(_context2, this), [position.current, drift, offset]);

  var _useSprings = useSprings(children.length, _bindInstanceProperty(_context4 = function _context4(i) {
    _newArrowCheck(this, _this);

    return {
      positionValue: getPositionValue(i),
      scale: 1,
      display: 'block'
    };
  }).call(_context4, this)),
      _useSprings2 = _slicedToArray(_useSprings, 2),
      springProps = _useSprings2[0],
      setSpringProps = _useSprings2[1];

  var setPosition = useCallback(_bindInstanceProperty(_context5 = function _context5(newPosition) {
    var _this2 = this,
        _context6;

    _newArrowCheck(this, _this);

    onNewPosition && onNewPosition(newPosition);
    position.current = newPosition;
    setSpringProps(_bindInstanceProperty(_context6 = function _context6(i) {
      _newArrowCheck(this, _this2);

      return {
        positionValue: getPositionValue(i)
      };
    }).call(_context6, this));
  }).call(_context5, this), [onNewPosition, position.current, getPositionValue]);
  var goTo = useCallback(_bindInstanceProperty(_context7 = function _context7(newPosition) {
    _newArrowCheck(this, _this);

    setPosition(clampPosition(newPosition));
  }).call(_context7, this), [clampPosition, setPosition]);
  var next = useCallback(_bindInstanceProperty(_context8 = function _context8() {
    _newArrowCheck(this, _this);

    return goTo(position.current + 1);
  }).call(_context8, this), [goTo, position.current]);
  var previous = useCallback(_bindInstanceProperty(_context9 = function _context9() {
    _newArrowCheck(this, _this);

    return goTo(position.current - 1);
  }).call(_context9, this), [goTo, position.current]);
  var globalStyles = globalTheme ? CarouselSlide.globalStyles[globalTheme] || {} : {};
  var globalClassNames = globalTheme ? CarouselSlide.globalClassNames[globalTheme] || {} : {};
  var mergedStyles = {
    root: mergeStyles(globalStyles.root, customStyles.root, style, {
      height: height,
      width: width,
      minHeight: minHeight,
      minWidth: minWidth,
      maxHeight: maxHeight,
      maxWidth: maxWidth,
      overflow: showOverflow ? 'visible' : 'hidden'
    }),
    item: mergeStyles(globalStyles.item, customStyles.item, {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      cursor: disableGestures ? null : isGrabbing ? 'grabbing' : 'grab'
    })
  };
  var mergedClassNames = {
    root: mergeClassNames(resetClassNames.root, componentClassNames.root, globalClassNames.root, customClassNames.root, className),
    item: mergeClassNames(componentClassNames.item, globalClassNames.item, customClassNames.item)
  };
  var bind = null;

  if (!disableGestures) {
    bind = useGesture({
      /**
       * @description Callback for clickCapture event.
       * @param {Object<string,*>} e - Triggering event.
       * @returns {void} Void.
       */
      onClickCapture: function onClickCapture(e) {
        if (isDragging.current) {
          e.cancelable && e.preventDefault();
          e.stopPropagation();
        }
      },

      /**
       * @description Callback for mouseDownCapture event.
       * @returns {void} Void.
       */
      onMouseDownCapture: function onMouseDownCapture() {
        setIsGrabbing(true);
      },

      /**
       * @description Callback for mouseUpCapture event.
       * @returns {void} Void.
       */
      onMouseUpCapture: function onMouseUpCapture() {
        setIsGrabbing(false);
      },

      /**
       * @description Callback for drag event.
       * @param {Object} arg - Arg.
       * @param {boolean} arg.down - Down.
       * @param {Array<number>} arg.movement - Movement.
       * @param {Array<number>} arg.direction - Direction.
       * @param {number} arg.distance - Movement.
       * @param {Function} arg.cancel - Cancel.
       * @returns {void} Void.
       */
      onDrag: function onDrag(_ref2) {
        var _this3 = this,
            _context11;

        var down = _ref2.down,
            _ref2$movement = _slicedToArray(_ref2.movement, 2),
            movX = _ref2$movement[0],
            movY = _ref2$movement[1],
            _ref2$direction = _slicedToArray(_ref2.direction, 2),
            dirX = _ref2$direction[0],
            dirY = _ref2$direction[1],
            distance = _ref2.distance,
            cancel = _ref2.cancel;

        var slideSize = isRow ? window.innerWidth : window.innerHeight;
        setIsGrabbing(down);

        if (!down) {
          var _context10;

          isDraggingTimer.current = _setTimeout(_bindInstanceProperty(_context10 = function _context10() {
            _newArrowCheck(this, _this3);

            isDragging.current = false;
          }).call(_context10, this), 500);
        } else {
          clearTimeout(isDraggingTimer.current);
          isDraggingTimer.current = null;
          isDragging.current = true;
        }

        if (rootRef.current) {
          slideSize = isRow ? rootRef.current.clientWidth : rootRef.current.clientHeight;
        }

        var mov = isRow ? movX : movY;
        var dir = isRow ? dirX : dirY;
        var switchThreshold = slideSize * (1 - drift) / 4;

        if (down && distance > switchThreshold) {
          cancel();
          setPosition(clampPosition(position.current + (dir > 0 ? -1 : 1)));
        }

        setSpringProps(_bindInstanceProperty(_context11 = function _context11(i) {
          _newArrowCheck(this, _this3);

          return {
            positionValue: getPositionValue(i, down ? mov : 0)
          };
        }).call(_context11, this));
      }
    }, {
      drag: {
        filterTaps: true
      }
    });
  }

  useEffect(_bindInstanceProperty(_context12 = function _context12() {
    _newArrowCheck(this, _this);

    onMount && onMount({
      goTo: goTo,
      next: next,
      previous: previous
    });
  }).call(_context12, this), [onMount, goTo, next, previous]);
  return /*#__PURE__*/React.createElement("div", {
    ref: rootRef,
    style: mergedStyles.root,
    className: mergedClassNames.root
  }, _mapInstanceProperty(springProps).call(springProps, _bindInstanceProperty(_context13 = function _context13(springProp, index) {
    _newArrowCheck(this, _this);

    return /*#__PURE__*/React.createElement(animated.div, _extends({
      key: index
    }, bind && bind() || {}, {
      style: mergeStyles(mergedStyles.item, _defineProperty({}, positionKey, springProp.positionValue)),
      className: mergedClassNames.item
    }), children[index]);
  }).call(_context13, this)));
}

CarouselSlide.defaultProps = {
  direction: 'row',
  startingPosition: 0,
  itemsPerView: 1,
  drift: 0,
  offset: 0,
  showOverflow: false,
  disableGestures: false,
  onMount: null,
  onNewPosition: null,
  children: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
};
CarouselSlide.globalStyles = {
  default: {
    root: {},
    item: {}
  }
};
CarouselSlide.globalClassNames = {
  default: {
    root: '',
    item: ''
  }
};
export { CarouselSlide };